import React, { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import ImageLightbox from "../components/ImageLightbox";
import { useAuth } from "../context/AuthContext";
import { fetchMyPlots, fetchIssues, fetchIssueCategories, createIssue, uploadIssuePhoto, closeIssue } from "../api/api";

const MAX_PHOTOS = 3;

function formatDateTime(iso) {
  return new Date(iso).toLocaleString(undefined, {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

function StarRating({ value, onChange }) {
  return (
    <div style={{ display: "flex", gap: "4px" }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span
          key={n}
          onClick={() => onChange(n)}
          style={{ cursor: "pointer", fontSize: "26px", color: n <= value ? "#f59e0b" : "#ddd" }}
        >
          ★
        </span>
      ))}
    </div>
  );
}

const STATUS_LABELS = {
  received: "Received",
  review: "Review",
  resolved: "Resolved",
  closed: "Closed",
};

const STATUS_STYLES = {
  received: { background: "#fef3c7", color: "#92400e" },
  review: { background: "#dbeafe", color: "#1e40af" },
  resolved: { background: "#dcfce7", color: "#166534" },
  closed: { background: "#e5e7eb", color: "#374151" },
};

function errorMessage(err) {
  return err.response?.data?.detail || "Something went wrong. Please try again.";
}

function CustomerIssues() {

  const { user } = useAuth();

  const [plots, setPlots] = useState([]);
  const [requests, setRequests] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [categoryId, setCategoryId] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [photoFiles, setPhotoFiles] = useState([]); // File objects picked, not yet uploaded
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");

  // Which resolved issue's "close" form is currently open, keyed by id —
  // only one at a time.
  const [closingId, setClosingId] = useState(null);
  const [closeNotes, setCloseNotes] = useState("");
  const [closeRating, setCloseRating] = useState(0);
  const [closePhotoUrls, setClosePhotoUrls] = useState([]);
  const [closeFileUrls, setCloseFileUrls] = useState([]);
  const [closeUploading, setCloseUploading] = useState(false);
  const [closeSubmitting, setCloseSubmitting] = useState(false);
  const [closeError, setCloseError] = useState("");

  // Which issue's history/attachments are expanded, keyed by id.
  const [expandedId, setExpandedId] = useState(null);
  const [viewerUrl, setViewerUrl] = useState(null);

  const plot = plots[0] || null;

  const load = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [plotList, requestList, categoryList] = await Promise.all([
        fetchMyPlots(),
        fetchIssues(),
        fetchIssueCategories(),
      ]);
      setPlots(plotList);
      setRequests(requestList);
      setCategories(categoryList.filter((c) => c.is_active));
    } catch (err) {
      setLoadError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    setFormSuccess("");

    if (!plot) {
      setFormError("You don't have a registered plot yet.");
      return;
    }
    if (!categoryId) {
      setFormError("Please select a category.");
      return;
    }
    if (!title.trim() || !description.trim()) {
      setFormError("Please add a title and description.");
      return;
    }

    setSubmitting(true);
    try {
      // Upload any attached photos first — the backend wants file_urls
      // ready before the complaint itself is created, same as the mobile
      // app's flow.
      const photoUrls = [];
      for (const file of photoFiles) {
        const url = await uploadIssuePhoto(file);
        photoUrls.push(url);
      }

      await createIssue({
        plot_id: plot.id,
        category_id: Number(categoryId),
        title: title.trim(),
        description: description.trim(),
        photo_urls: photoUrls,
      });
      setTitle("");
      setDescription("");
      setPhotoFiles([]);
      setFormSuccess("Your issue has been raised — the team has been notified.");
      await load();
    } catch (err) {
      setFormError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handlePhotoSelect = (e) => {
    const chosen = Array.from(e.target.files || []);
    setPhotoFiles((prev) => [...prev, ...chosen].slice(0, MAX_PHOTOS));
    e.target.value = ""; // allow re-picking the same file
  };

  const removePhoto = (index) => {
    setPhotoFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const startClose = (issueId) => {
    setClosingId(issueId);
    setCloseNotes("");
    setCloseRating(0);
    setClosePhotoUrls([]);
    setCloseFileUrls([]);
    setCloseError("");
  };

  const handleCloseAttachment = async (e, kind) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setCloseUploading(true);
    setCloseError("");
    try {
      const url = await uploadIssuePhoto(file, closingId);
      if (kind === "photo") setClosePhotoUrls((prev) => [...prev, url]);
      else setCloseFileUrls((prev) => [...prev, url]);
    } catch (err) {
      setCloseError(errorMessage(err));
    } finally {
      setCloseUploading(false);
    }
  };

  const submitClose = async () => {
    if (!closeRating) {
      setCloseError("Please rate how the issue was resolved.");
      return;
    }
    setCloseSubmitting(true);
    setCloseError("");
    try {
      const updated = await closeIssue(closingId, closeNotes.trim() || undefined, closeRating, closePhotoUrls, closeFileUrls);
      setRequests((prev) => prev.map((r) => (r.id === closingId ? updated : r)));
      setClosingId(null);
    } catch (err) {
      setCloseError(errorMessage(err));
    } finally {
      setCloseSubmitting(false);
    }
  };

  return (
    <>
      <Navbar />
      <Sidebar />

      <div className="main-content">

        <div className="customer-request-page">

          <h2>Issues</h2>

          {loading && <p>Loading…</p>}
          {loadError && <p className="login-error" style={{ color: "red" }}>{loadError}</p>}

          {!loading && !loadError && (
            <>

              <div className="request-form-card">

                <h3>Raise a New Issue</h3>

                {!plot && (
                  <p style={{ color: "#991b1b" }}>You don't have a registered plot yet — contact Jain Farms to get one set up before raising a request.</p>
                )}

                {formError && <p className="login-error" style={{ color: "red" }}>{formError}</p>}
                {formSuccess && <p style={{ color: "#166534", fontWeight: 600 }}>{formSuccess}</p>}

                <form onSubmit={handleSubmit}>

                  <label>Plot Number</label>
                  <input type="text" value={plot?.plot_number || ""} disabled />

                  <label>Customer Name</label>
                  <input type="text" value={user?.full_name || ""} disabled />

                  <label>Date</label>
                  <input type="text" value={new Date().toLocaleString()} disabled />

                  <label>Category</label>
                  <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} required>
                    <option value="" disabled>Select category</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>

                  <label>Title</label>
                  <input
                    type="text"
                    placeholder="Brief title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                  />

                  <label>Description</label>
                  <textarea
                    rows="4"
                    placeholder="Describe the issue…"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    required
                  />

                  <label>Photos (optional, up to {MAX_PHOTOS})</label>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoSelect}
                    disabled={photoFiles.length >= MAX_PHOTOS}
                  />

                  {photoFiles.length > 0 && (
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", margin: "8px 0" }}>
                      {photoFiles.map((file, i) => (
                        <div key={i} style={{ position: "relative" }}>
                          <img
                            src={URL.createObjectURL(file)}
                            alt={`Attachment ${i + 1}`}
                            style={{ width: "64px", height: "64px", objectFit: "cover", borderRadius: "6px", border: "1px solid #ddd" }}
                          />
                          <button
                            type="button"
                            onClick={() => removePhoto(i)}
                            title="Remove"
                            style={{
                              position: "absolute", top: "-6px", right: "-6px", width: "20px", height: "20px",
                              borderRadius: "10px", border: "none", background: "#dc2626", color: "white",
                              fontSize: "12px", lineHeight: "20px", cursor: "pointer", padding: 0,
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <button type="submit" className="submit-request-btn" disabled={submitting || !plot}>
                    {submitting ? "Submitting…" : "Report Issue"}
                  </button>
                </form>

              </div>

              <div className="my-requests-card">

                <h3>My Requests</h3>

                {requests.length === 0 && <p style={{ color: "#666" }}>No requests raised yet.</p>}

                {requests.map((r) => {
                  const expanded = expandedId === r.id;
                  return (
                  <div
                    key={r.id}
                    className="request-item"
                    style={{ display: "block", cursor: "pointer" }}
                    onClick={() => setExpandedId(expanded ? null : r.id)}
                  >

                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <strong>{r.title}</strong>
                        <p>{r.ticket_number} • {new Date(r.complaint_date).toLocaleDateString()}</p>
                      </div>

                      <span
                        style={{
                          ...(STATUS_STYLES[r.status] || {}),
                          padding: "3px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: 600,
                        }}
                      >
                        {STATUS_LABELS[r.status] || r.status}
                      </span>
                    </div>

                    <p style={{ margin: "6px 0", color: "#555" }}>{r.description}</p>

                    <p style={{ margin: 0, fontSize: "12px", color: "#1B4D2E", fontWeight: 600 }}>
                      {expanded ? "▲ Hide details" : "▼ View details"}
                    </p>

                    {expanded && r.status_history?.length > 0 && (
                      <div style={{ marginTop: "8px" }}>
                        {r.status_history.map((h) => (
                          <div key={h.id} style={{ fontSize: "13px", background: "#f5f5f5", padding: "8px", borderRadius: "6px", marginBottom: "6px" }}>
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                              <strong>{STATUS_LABELS[h.status] || h.status}</strong>
                              <span style={{ color: "#888" }}>{formatDateTime(h.created_at)}</span>
                            </div>
                            {h.notes && <p style={{ margin: "4px 0 0" }}>{h.notes}</p>}
                            {h.rating && (
                              <p style={{ margin: "4px 0 0", color: "#f59e0b" }}>{"★".repeat(h.rating)}{"☆".repeat(5 - h.rating)}</p>
                            )}
                            {h.attachments?.length > 0 && (
                              <div style={{ display: "flex", gap: "8px", marginTop: "6px", flexWrap: "wrap" }}>
                                {h.attachments.map((a) => (
                                  a.kind === "photo" ? (
                                    <img
                                      key={a.id} src={a.file_url} alt="Attachment"
                                      onClick={(e) => { e.stopPropagation(); setViewerUrl(a.file_url); }}
                                      style={{ width: "56px", height: "56px", objectFit: "cover", borderRadius: "6px", border: "1px solid #ddd", cursor: "pointer" }}
                                    />
                                  ) : (
                                    <a key={a.id} href={a.file_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>📎 {a.file_url.split("/").pop()}</a>
                                  )
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {r.status === "resolved" && closingId !== r.id && (
                      <button
                        type="button"
                        className="submit-request-btn"
                        style={{ marginTop: "8px" }}
                        onClick={(e) => { e.stopPropagation(); startClose(r.id); }}
                      >
                        Close Issue
                      </button>
                    )}

                    {closingId === r.id && (
                      <div
                        style={{ marginTop: "10px", border: "1px solid #ddd", borderRadius: "8px", padding: "10px" }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {closeError && <p className="login-error" style={{ color: "red" }}>{closeError}</p>}

                        <label>How was it resolved? <span style={{ color: "red" }}>*</span></label>
                        <StarRating value={closeRating} onChange={setCloseRating} />

                        <label style={{ display: "block", marginTop: "8px" }}>Message (optional)</label>
                        <textarea
                          rows="3"
                          style={{ width: "100%" }}
                          value={closeNotes}
                          onChange={(e) => setCloseNotes(e.target.value)}
                          placeholder="Anything else you'd like to add…"
                        />

                        <label style={{ display: "block", marginTop: "8px" }}>Photos (optional)</label>
                        <input type="file" accept="image/*" onChange={(e) => handleCloseAttachment(e, "photo")} disabled={closeUploading || closePhotoUrls.length >= MAX_PHOTOS} />
                        {closePhotoUrls.length > 0 && (
                          <div style={{ display: "flex", gap: "8px", marginTop: "6px", flexWrap: "wrap" }}>
                            {closePhotoUrls.map((url) => (
                              <img
                                key={url} src={url} alt="Attachment"
                                onClick={() => setViewerUrl(url)}
                                style={{ width: "56px", height: "56px", objectFit: "cover", borderRadius: "6px", cursor: "pointer" }}
                              />
                            ))}
                          </div>
                        )}

                        <label style={{ display: "block", marginTop: "8px" }}>Files (optional)</label>
                        <input type="file" onChange={(e) => handleCloseAttachment(e, "file")} disabled={closeUploading || closeFileUrls.length >= MAX_PHOTOS} />
                        {closeFileUrls.length > 0 && (
                          <div style={{ marginTop: "6px" }}>
                            {closeFileUrls.map((url) => (
                              <div key={url}>📎 {url.split("/").pop()}</div>
                            ))}
                          </div>
                        )}

                        <div style={{ display: "flex", gap: "8px", marginTop: "10px" }}>
                          <button type="button" className="submit-request-btn" onClick={submitClose} disabled={closeSubmitting}>
                            {closeSubmitting ? "Closing…" : "Submit & Close"}
                          </button>
                          <button type="button" className="view-btn" onClick={() => setClosingId(null)} disabled={closeSubmitting}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}

                  </div>
                  );
                })}

              </div>

            </>
          )}

        </div>

      </div>

      <ImageLightbox url={viewerUrl} onClose={() => setViewerUrl(null)} />
    </>
  );
}

export default CustomerIssues;
